import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import Button from '../Button'
import Select from '../Select'
import Form from '../Form'
import Input from '../Input'
import Radio from '../Radio'
import Checkbox from '../Checkbox'
import Switch from '../Switch'
import { NvSocket, NvApi } from '../../Services'
import { useLocalStorage, ValidatorUtils } from '../../Utils'
import { getHcaptchaRunner } from '../../Hcaptcha'
import {
  Widget,
  addUserMessage,
  setQuickButtons,
  renderCustomComponent,
  toggleMsgLoader,
  setBadgeCount,
  addResponseMessage,
  deleteMessages,
  isWidgetOpened,
  toggleWidget
} from 'react-chat-widget'
import {
  StyledTimestamp,
  StyledWidgetContainer,
  StyledAgentContainer,
  StyledResponseContainer,
  StyledMessageContainer,
  StyledMessage,
  StyledMenuOptionsContainer,
  StyledQuickReplyOptionsContainer,
  StyledDropdown,
  StyledDropdownOption,
  StyledCarousel,
  StyledCarouselOptionContainer,
  StyledCarouselOption,
  StyledCarouselIcon,
  StyledLauncherButton,
  StyledLauncherIcon,
  StyledAttachmentContainer,
  StyledAttachmentIcon,
  StyledFormContainer,
  StyledFormTitle,
  StyledSectionTitle,
  StyledFormSectionContainer,
  StyledFormItem,
  StyledFormInputOptionContainer,
  StyledFormInput,
  StyledFormSubmitButton
} from './styles'
import { BASE_WIDGET_NAME, CUSTOM_WIDGET_NAMES } from './constants'
import {
  faAngleLeft,
  faAngleRight,
  faCommentDots,
  faTimes,
  faFileVideo,
  faFilePdf,
  faFileAudio,
  faFileImage,
  faFileExclamation,
  faFileWord
} from '@fortawesome/pro-regular-svg-icons'

import 'react-chat-widget/lib/styles.css'

const { Option } = Select
const { TextArea } = Input

const BUTTON_TYPES = {
  DEFAULT: 'DEFAULT',
  MENU: 'MENU',
  FIXED: 'FIXED',
  VARIABLE: 'VARIABLE',
  USER_IDENTITY: 'USER_IDENTITY',
  URL: 'URL'
}
const SOCKET_EVENTS = {
  CONNECT: 'connect',
  MESSAGE: 'message',
  RECONNECT: 'reconnect'
}
const DROPDOWN_OPTION_THRESHOLD = 8
const RYO_AVATAR = 'https://storage.googleapis.com/storage.ninjavan.co/sns/static/NinjaChat-01.png'
const TYPING_ON_TEMPLATE_ID = 500052
const TYPING_OFF_TEMPLATE_ID = 500053
const MAX_TYPING_INDICATOR_DURATION_MS = 5000
const URL_REGEX = /(\b(\shttps?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi

export const ERROR_INVALID_CAPTCHA_TOKEN = 'invalid-captcha-token'

let showTyping = false
const toggleOnTyping = () => {
  if (!showTyping) {
    showTyping = true
    toggleMsgLoader()
  }
}
const toggleOffTyping = () => {
  if (showTyping) {
    showTyping = false
    toggleMsgLoader()
  }
}

let badgeCount = 0
let lastMessage = {}
let hasBeenOpened = false
let socket
let snsApi
let ninjachatClientId
let ninjachatCaptchaToken

const getMainText = (text, attachments, fitContent) => (
  <StyledMessageContainer>
    <StyledMessage fitContent={fitContent}>
      {text.split('\n').map(line => (
        <p
          key={line}
          dangerouslySetInnerHTML={{
            __html: line
              ? line.replace(URL_REGEX, url => '<a href="' + url + '" target="_blank">' + url + '</a>')
              : '<br />'
          }}
        />
      ))}
      {getAttachments(attachments)}
    </StyledMessage>
  </StyledMessageContainer>
)

const getAttachments = attachments => {
  return attachments.length > 0 ? (
    <StyledAttachmentContainer>
      {attachments.map(attachment => (
        <a href={attachment.url} target={'_blank'}>
          <StyledAttachmentIcon
            data-testid='attachment-icon'
            icon={(() => {
              switch (attachment.type) {
                case 'audio':
                  return faFileAudio
                case 'image':
                  return faFileImage
                case 'text':
                  return faFileWord
                case 'video':
                  return faFileVideo
                case 'application':
                  return faFilePdf
                default:
                  return faFileExclamation
              }
            })()}
          />
        </a>
      ))}
    </StyledAttachmentContainer>
  ) : null
}

const asMinTwoDigits = num => num.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })

const isWithinSameMinute = (time1, time2) => {
  // This precision should suffice
  return (
    time1.getMonth() === time2.getMonth() &&
    time1.getDate() === time2.getDate() &&
    time1.getHours() === time2.getHours() &&
    time1.getMinutes() === time2.getMinutes()
  )
}

const getCustomTimestamp = msgTime => {
  const time = asMinTwoDigits(msgTime.getHours() % 12) + ':' + asMinTwoDigits(msgTime.getMinutes())

  return <StyledTimestamp className='rcw-timestamp'>{time}</StyledTimestamp>
}

const getResponse = (props, buttons) => {
  const { text, attachments, showNew, fitContent = false } = props
  return (
    <StyledAgentContainer>
      <div style={{ width: '100%' }}>
        {showNew && <img src={RYO_AVATAR} className='rcw-avatar' style={{ position: 'absolute' }} alt='ryo' />}
        <StyledResponseContainer>
          {getMainText(text, attachments, fitContent)}
          {buttons}
        </StyledResponseContainer>
      </div>
    </StyledAgentContainer>
  )
}

const FormResponse = props => {
  const { formConfig, form, source, time, options = [] } = props
  const { getFieldDecorator, validateFields } = form

  const [validating, setValidating] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [visible, setVisible] = useState(true)

  return getResponse(
    props,
    submitted ? null : (
      <div>
        <StyledFormContainer>
          {formConfig.name && <StyledFormTitle>{formConfig.name}</StyledFormTitle>}
          <Form
            onSubmit={e => {
              e.preventDefault()
              validateFields((err, values) => {
                if (!err) {
                  setValidating(true)

                  const formData = {}
                  const getKeyWithoutTimestamp = key => key.match(/^[0-9]+:(.+)/)[1] || key
                  for (const [key, value] of Object.entries(values)) {
                    if (Array.isArray(value)) {
                      formData[getKeyWithoutTimestamp(key)] = value.join(',')
                    } else {
                      formData[getKeyWithoutTimestamp(key)] = value
                    }
                  }

                  snsApi
                    .post('widget/form', {
                      id: formConfig.id,
                      event: formConfig.submitEvent,
                      source,
                      ...buildUserRequest(props),
                      data: formData
                    })
                    .then(response => {
                      if (response.status !== 200) {
                        if (response.data && response.data.error) {
                          addResponseMessage(_.replace(response.data.error, /\n/g, '\\\n'))
                        } else {
                          addResponseMessage('Unexpected error occurred. Please try again later.')
                        }
                      } else {
                        setSubmitted(true)
                      }
                    })
                    .catch(error => {
                      addResponseMessage(`Unexpected error '${error.data}' occurred. Please try again later.`)
                    })
                    .then(() => {
                      setValidating(false)
                    })
                }
              })
            }}
          >
            {formConfig.sections.map(section => (
              <StyledFormSectionContainer>
                {section.name && <StyledSectionTitle>{section.name}</StyledSectionTitle>}
                {section.fields.map(field => (
                  <StyledFormItem
                    label={
                      <span>
                        {field.name}
                        {!field.optional && <span style={{ color: '#e40712' }}> *</span>}
                      </span>
                    }
                    key={field.key}
                  >
                    {getFieldDecorator(time.getTime() + ':' + field.key, {
                      initialValue: field.defaultValue,
                      rules: [
                        {
                          required: !field.optional,
                          message: '*Required'
                        },
                        (() => {
                          switch (field.type) {
                            case 'contact':
                              return {
                                validator: ValidatorUtils.phoneNumber,
                                message: 'Invalid contact'
                              }
                            case 'email':
                              return {
                                type: 'email',
                                message: 'Invalid email'
                              }
                            case 'checkbox':
                              return {
                                transform: arrValue => (arrValue ? arrValue.join(',') : arrValue)
                              }
                            case 'switch':
                              return {
                                type: 'boolean'
                              }
                            default:
                              return {}
                          }
                        })()
                      ]
                    })(
                      (() => {
                        switch (field.type) {
                          case 'text':
                          case 'contact':
                            return (
                              <StyledFormInput disabled={submitted} maxLength={field.key === 'tracking_id' ? 30 : ''} />
                            )
                          case 'number':
                            return <StyledFormInput type='number' disabled={submitted} />
                          case 'email':
                            return <StyledFormInput type='email' disabled={submitted} />
                          case 'text_area':
                            return <TextArea style={{ height: '128px', fontSize: '14px' }} disabled={submitted} />
                          case 'date':
                            return <StyledFormInput type='date' disabled={submitted} />
                          case 'time':
                            return <StyledFormInput type='time' disabled={submitted} />
                          case 'select':
                            return (
                              <Select disabled={submitted}>
                                {field.options.map(option => (
                                  <Option value={option.value} key={option.value}>
                                    {option.label}
                                  </Option>
                                ))}
                              </Select>
                            )
                          case 'radio':
                            return (
                              <Radio.Group disabled={submitted}>
                                <StyledFormInputOptionContainer>
                                  {field.options.map(option => (
                                    <Radio value={option.value} key={option.value}>
                                      {option.label}
                                    </Radio>
                                  ))}
                                </StyledFormInputOptionContainer>
                              </Radio.Group>
                            )
                          case 'checkbox':
                            return (
                              <Checkbox.Group disabled={submitted}>
                                <StyledFormInputOptionContainer>
                                  {field.options.map(option => (
                                    <Checkbox value={option.value} key={option.value}>
                                      {option.label}
                                    </Checkbox>
                                  ))}
                                </StyledFormInputOptionContainer>
                              </Checkbox.Group>
                            )
                          case 'switch':
                            return <Switch disabled={submitted} />
                          default:
                            return <StyledFormInput disabled={submitted} />
                        }
                      })()
                    )}
                  </StyledFormItem>
                ))}
              </StyledFormSectionContainer>
            ))}
            <StyledFormItem>
              <StyledFormSubmitButton htmlType='submit' loading={validating} disabled={submitted}>
                {validating ? 'Submitting...' : 'Submit'}
              </StyledFormSubmitButton>
            </StyledFormItem>
          </Form>
        </StyledFormContainer>
        {getCustomTimestamp(time)}
        <StyledQuickReplyOptionsContainer>
          {visible
            ? options.map(option => (
                <Button
                  className='ninjachat-quick-reply-btn'
                  shape='round'
                  key={option.payload}
                  onClick={() => {
                    setVisible(false)
                    handleButtonClick(props)(option)
                  }}
                >
                  {option.title || option.body}
                </Button>
              ))
            : null}
        </StyledQuickReplyOptionsContainer>
      </div>
    )
  )
}

FormResponse.propTypes = {
  formConfig: PropTypes.object,
  form: PropTypes.object,
  source: PropTypes.string
}

const MenuButtonsResponse = props => {
  const { options = [], time } = props

  return getResponse(
    props,
    <div>
      <StyledMenuOptionsContainer>
        {options.map(option => (
          <Button
            className='ninjachat-menu-btn'
            block
            key={option.payload}
            onClick={() => handleButtonClick(props)(option)}
          >
            {option.title || option.body}
          </Button>
        ))}
      </StyledMenuOptionsContainer>
      {getCustomTimestamp(time)}
    </div>
  )
}

MenuButtonsResponse.propTypes = {
  text: PropTypes.string,
  options: PropTypes.array
}

const CarouselNext = props => {
  const { className, style, onClick } = props
  return (
    <div
      role='button'
      tabIndex='0'
      className={className}
      style={{ ...style, display: 'block' }}
      onClick={onClick}
      onKeyDown={onClick}
    >
      <StyledCarouselIcon icon={faAngleRight} />
    </div>
  )
}

CarouselNext.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func
}

const CarouselPrev = props => {
  const { className, style, onClick } = props
  return (
    <div
      role='button'
      tabIndex='0'
      className={className}
      style={{ ...style, display: 'block', left: '-42px' }}
      onClick={onClick}
      onKeyDown={onClick}
    >
      <StyledCarouselIcon icon={faAngleLeft} />
    </div>
  )
}

CarouselPrev.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  onClick: PropTypes.func
}

const CarouselButtonsResponse = props => {
  const { options = [], time } = props
  const settings = {
    nextArrow: <CarouselNext />,
    prevArrow: <CarouselPrev />
  }

  return getResponse(
    props,
    <div>
      <StyledCarousel arrows {...settings} dots={false}>
        {options.map(option => (
          <StyledCarouselOptionContainer key={option.payload}>
            <StyledCarouselOption size='small' title={option.title}>
              {option.body}
            </StyledCarouselOption>
            <Button className='ninjachat-menu-btn' block onClick={() => handleButtonClick(props)(option)}>
              Select
            </Button>
          </StyledCarouselOptionContainer>
        ))}
      </StyledCarousel>
      {getCustomTimestamp(time)}
    </div>
  )
}

CarouselButtonsResponse.propTypes = {
  text: PropTypes.string,
  options: PropTypes.array
}

const DropdownSelection = props => {
  const { options } = props
  const [visible, setVisible] = useState(true)

  return visible ? (
    <StyledDropdown
      data-testid='dropdown'
      placeholder='Select option...'
      dropdownStyle={{ zIndex: 9999 }}
      onChange={option => {
        setVisible(false)
        handleButtonClick(props)(option)
      }}
      getPopupContainer={trigger => trigger.parentNode}
    >
      {options.map(option => (
        <Option key={option.payload} value={option} style={{ padding: '0px', backgroundColor: 'unset' }}>
          <StyledDropdownOption>{option.title || option.body}</StyledDropdownOption>
        </Option>
      ))}
    </StyledDropdown>
  ) : null
}

DropdownSelection.propTypes = {
  options: PropTypes.array
}

const QuickReplyButtonsResponse = props => {
  const { options = [], time, isUrl } = props
  const [visible, setVisible] = useState(true)

  return getResponse(
    props,
    <div>
      {getCustomTimestamp(time)}
      {visible && (
        <StyledQuickReplyOptionsContainer>
          {options.length > DROPDOWN_OPTION_THRESHOLD ? (
            <DropdownSelection {...props} />
          ) : (
            options.map(option => (
              <Button
                className='ninjachat-quick-reply-btn'
                shape='round'
                key={option.payload}
                href={isUrl ? option.payload : null}
                target={isUrl ? '_blank' : null}
                onClick={
                  isUrl
                    ? () => null
                    : () => {
                        setVisible(false)
                        handleButtonClick(props)(option)
                      }
                }
              >
                {option.title || option.body}
              </Button>
            ))
          )}
        </StyledQuickReplyOptionsContainer>
      )}
    </div>
  )
}

QuickReplyButtonsResponse.propTypes = {
  text: PropTypes.string,
  options: PropTypes.array,
  isUrl: PropTypes.bool
}

const registerSocketHandlers = (props, rerunCaptchaStartEvent, emitStartEvent) => {
  const { url, token = '', globalShipperId = 0, systemId, showFullScreen = false } = props

  if (socket) socket.disconnect()
  socket = NvSocket.create({
    url,
    token,
    query: {
      client_id: ninjachatClientId,
      global_shipper_id: globalShipperId
    }
  })
  snsApi = NvApi.create({
    country: systemId || 'global',
    service: 'sns',
    timeout: 30000
  }).api

  const registerMessageHandler = () => {
    socket.removeAllListeners(SOCKET_EVENTS.MESSAGE)

    socket.on(SOCKET_EVENTS.MESSAGE, data => {
      if (data.status === 'error' && data.text === ERROR_INVALID_CAPTCHA_TOKEN) {
        rerunCaptchaStartEvent(true)
        return
      }
      if (data.request_id) {
        // Propagated from another client for the same client
        data.message && addUserMessage(data.message)

        // restoring user texts from previous session
        if (data.request_id === 'user' && data.text) {
          addUserMessage(data.text)
        }
      } else {
        const { options = [], templateId = 0 } = data
        const message = {
          text: data.text,
          attachments: data.attachments || [],
          options,
          formConfig: data.form,
          time: data.sent_at ? new Date(data.sent_at) : new Date(),
          fitContent: data.display_type === BUTTON_TYPES.FIXED && data.options.length <= DROPDOWN_OPTION_THRESHOLD
        }

        if (templateId === TYPING_ON_TEMPLATE_ID) {
          toggleOnTyping()
          setTimeout(() => toggleOffTyping(), MAX_TYPING_INDICATOR_DURATION_MS)

          // Move the indicator in view (with scroll)
          addResponseMessage('', 'typing')
          deleteMessages(1, 'typing')
        } else if (templateId === TYPING_OFF_TEMPLATE_ID) {
          toggleOffTyping()
        } else if (data.text || data.form || data.attachments.length > 0 || options.length > 0) {
          toggleOffTyping()

          if (!isWidgetOpened()) {
            badgeCount++
            setBadgeCount(badgeCount)
          }

          const showNew = !lastMessage.time || !isWithinSameMinute(lastMessage.time, message.time)
          lastMessage = message

          if (message.formConfig && message.formConfig.id) {
            renderCustomComponent(FormResponse, { ...message, ...props, showNew })
          } else {
            switch (data.display_type) {
              case BUTTON_TYPES.MENU:
                renderCustomComponent(MenuButtonsResponse, { ...message, ...props, showNew })
                break
              case BUTTON_TYPES.VARIABLE:
                renderCustomComponent(CarouselButtonsResponse, { ...message, ...props, showNew })
                break
              case BUTTON_TYPES.URL:
                renderCustomComponent(QuickReplyButtonsResponse, { ...message, isUrl: true, ...props, showNew })
                break
              default:
                renderCustomComponent(QuickReplyButtonsResponse, { ...message, isUrl: false, ...props, showNew })
                break
            }
          }
        }
      }
    })
  }

  socket.on(SOCKET_EVENTS.CONNECT, () => {
    if (showFullScreen) {
      emitStartEvent()
    } else if (!hasBeenOpened && props.startEvent && props.triggerOnInitialLoad) {
      if (!isWidgetOpened()) toggleWidget()
    }
    registerMessageHandler()
  })
  socket.on(SOCKET_EVENTS.RECONNECT, registerMessageHandler)
}

const handleNewUserMessage = props => async newMessage => {
  setQuickButtons([])
  let messagePayload = newMessage
  const attachments = []

  if (props.showUploadAttachment && messagePayload.includes('![vertical]')) {
    let messagePayloadArr = messagePayload
      .replace('![vertical](', '')
      .replace(')', '')
      .split(/\[(img|video|application|txt)\/mime\]/)
    const blobPath = messagePayloadArr[0]
    const fileName = messagePayloadArr[2]
    const response = await fetch(blobPath)
    const blobFile = await response.blob()

    if (validFile(blobFile, fileName, props)) {
      let uploadResponse = await props.uploadFunction(blobFile, fileName)
      if (uploadResponse) {
        attachments.push(uploadResponse)
        messagePayload = 'Attachment Received'
      } else {
        // If file failed to upload, delete the file from the message store so it does not render on chat-widget
        deleteMessages(1)
      }
    }
  }

  socket.emit(SOCKET_EVENTS.MESSAGE, buildUserRequest(props, messagePayload, messagePayload, attachments))
  lastMessage = {}
}

const errorType = {
  UnsupportedFileFormat: 'unsupported_file_format',
  FileSizeTooLarge: 'filesize_too_large'
}

const validFile = (blobFile, fileName, props) => {
  const allowedFileFormats = ['png', 'jpeg', 'jpg', 'mp4', 'avi', 'xls', 'xlsx', 'csv', 'pdf', 'txt', 'gif']
  if (!allowedFileFormats.includes(fileName.split('.').pop())) {
    // Delete invalid files rendering in chat-widget
    deleteMessages(1)
    props.onFileUploadError(errorType.UnsupportedFileFormat)
    return
  }
  // maxFileSize prop calculated as MB
  if (blobFile.size / (1024 * 1024) > props.maxFileSize) {
    deleteMessages(1)
    props.onFileUploadError(errorType.FileSizeTooLarge)
    return
  }
  return true
}

const handleButtonClick = props => option => {
  setQuickButtons([])
  addUserMessage(option.title || option.body)
  socket.emit(SOCKET_EVENTS.MESSAGE, buildUserRequest(props, option.title || option.body, option.payload))
  lastMessage = {}
}

const buildUserRequest = (props, message, payload, attachments = []) => {
  const { systemId, languageCode = '', globalShipperId = 0, phoneNumber = '', shipperName = '' } = props

  return {
    system_id: systemId,
    language_code: languageCode,
    request_id: uuidv4(),
    client_id: ninjachatClientId,
    global_shipper_id: globalShipperId,
    phone_number: phoneNumber,
    shipper_name: shipperName,
    message_ref: uuidv4(),
    message,
    payload,
    captcha_token: ninjachatCaptchaToken,
    attachments: attachments
  }
}

const NinjaChatWidget = props => {
  const {
    startEvent,
    globalShipperId,
    hcaptchaConfig,
    captchaEnabled = false,
    source = '',
    systemId = '',
    showFullScreen = false,
    showCloseButton = true,
    showUploadAttachment = false,
    handleToggle = () => null
  } = props
  const newClientId = uuidv4()
  const [clientId, setClientId] = useLocalStorage('ninjachatClientId', newClientId)

  if (!isWidgetOpened() && showFullScreen) toggleWidget()

  if (clientId === newClientId) setClientId(newClientId)
  ninjachatClientId = clientId

  const emitStartEventWithToken = token => {
    let query = `trigger:${startEvent}`
    if (token) {
      query = `trigger:CERK:${startEvent}:${token}`
    }
    ninjachatCaptchaToken = token
    socket.emit(SOCKET_EVENTS.MESSAGE, buildUserRequest(props, startEvent, query))
    toggleOnTyping()
    setTimeout(() => toggleOffTyping(), MAX_TYPING_INDICATOR_DURATION_MS)
    return { data: { isRecaptchaOk: true } }
  }

  const emitStartEvent = () => {
    if (captchaEnabled && hcaptchaConfig) {
      getHcaptchaRunner(hcaptchaConfig).run(emitStartEventWithToken, 'StartEvent')
    } else {
      emitStartEventWithToken(null)
    }
  }

  const rerunCaptchaStartEvent = captchaError => {
    if (captchaEnabled && hcaptchaConfig) {
      if (captchaError) {
        getHcaptchaRunner(hcaptchaConfig).resetToken()
      }
      getHcaptchaRunner(hcaptchaConfig).run(emitStartEventWithToken, 'StartEvent')
    }
  }

  useEffect(() => {
    setClientId(newClientId)
    registerSocketHandlers(props, rerunCaptchaStartEvent, emitStartEvent)
    deleteMessages()
    badgeCount = 0
    lastMessage = {}
    hasBeenOpened = false
    if (isWidgetOpened() && !showFullScreen) toggleWidget()
  }, [globalShipperId, startEvent, showFullScreen])

  const { openIcon = faCommentDots, closeIcon = faTimes } = props
  const getCustomLauncher = handleToggle => {
    if (isWidgetOpened()) {
      if (socket && socket.connected && !hasBeenOpened && startEvent) {
        hasBeenOpened = true

        emitStartEvent()
      }

      return (
        <StyledLauncherButton
          className='rcw-launcher rcw-hide-sm'
          onClick={e => {
            badgeCount = 0
            setBadgeCount(badgeCount)
            handleToggle(e)
          }}
        >
          <StyledLauncherIcon icon={closeIcon} className='rcw-close-launcher' />
        </StyledLauncherButton>
      )
    }

    return (
      <StyledLauncherButton
        className='rcw-launcher'
        onClick={e => {
          badgeCount = 0
          setBadgeCount(badgeCount)
          handleToggle(e)
        }}
      >
        {badgeCount > 0 && <span className='rcw-badge'>{badgeCount}</span>}
        <StyledLauncherIcon icon={openIcon} className='rcw-open-launcher' />
      </StyledLauncherButton>
    )
  }

  return (
    <StyledWidgetContainer>
      <Widget
        title={(CUSTOM_WIDGET_NAMES[source] || {})[systemId] || BASE_WIDGET_NAME}
        subtitle='1.0'
        profileAvatar={RYO_AVATAR}
        handleNewUserMessage={handleNewUserMessage(props)}
        showCloseButton={showFullScreen ? false : showCloseButton}
        launcher={showFullScreen ? ignored => null : getCustomLauncher}
        fullScreenMode={showFullScreen}
        isShowFileUploader={showUploadAttachment}
        handleToggle={state => handleToggle(state)}
      />
    </StyledWidgetContainer>
  )
}

NinjaChatWidget.propTypes = {
  startEvent: PropTypes.string,
  captchaEnabled: PropTypes.bool,
  hcaptchaConfig: PropTypes.object
}

export default Form.create()(NinjaChatWidget)
